import { ApiError, hasHistoryContext, readSse } from './api.js';

// ════════════════════════════════════════════════════════════════════
// Centre de notifications des agents (P1, contrat §4, §5 et §6bis) : liste, lecture,
// accusé d'une alerte, flux en temps réel, Web Push du navigateur et état du moteur.
// Le demandeur est identifié par les en-têtes X-Entreprise-Id et X-User-Api-Hash, jamais
// dans l'URL (journaux nginx) : le flux est donc lu par fetch et readSse, jamais par
// EventSource, qui ne pose pas d'en-têtes. Seul le curseur du flux (`apres`, rang d'une
// notification, valeur non sensible) passe dans l'URL. Réponses jamais mises en cache.
// Sur le modèle d'emailRequest (services/api.js), sans le modifier : la base des URL et les
// en-têtes de contexte, non exportés là-bas, sont recopiés ici (comme agentsApi.js).
// ════════════════════════════════════════════════════════════════════

// import.meta.env n'existe que sous Vite : le module reste importable par les tests node
const API_BASE_URL = (import.meta.env && import.meta.env.VITE_API_URL) || 'http://localhost:3001';

// Seul événement du flux des notifications ; les commentaires « : ping » (toutes les 25 s,
// contre la coupure de nginx après 600 s sans octet) sont ignorés par readSse
export const EVENEMENTS_FLUX = new Set(['notification']);

function contextHeaders(context) {
  return {
    'X-Entreprise-Id': String(context.entreprise_id),
    'X-User-Api-Hash': String(context.user_api_hash),
  };
}

const enc = (value) => encodeURIComponent(String(value));

const exigerContexte = (context) => {
  if (!hasHistoryContext(context)) {
    throw new ApiError('Ouvrez l’assistant depuis la plateforme digiTRANS pour recevoir vos notifications.', { status: 401, code: 'CONTEXT_REQUIRED' });
  }
};

// Connexion coupée (fetch lève un TypeError) : statut 0, code NETWORK ; annulation relancée telle quelle
async function envoyer(url, init) {
  try {
    return await fetch(url, { cache: 'no-store', ...init });
  } catch (error) {
    if (error?.name === 'AbortError') throw error;
    throw new ApiError('Connexion au serveur interrompue.', { status: 0, code: 'NETWORK' });
  }
}

const erreurHttp = (response, data) =>
  new ApiError(data?.error || `HTTP ${response.status}`, { status: response.status, code: data?.code ?? null });

/**
 * Requête JSON. `contexte` : en-têtes de contexte exigés (sans contexte complet, ApiError 401
 * CONTEXT_REQUIRED et rien ne part) ; false : route publique ou protégée par un jeton.
 */
async function requete(path, context, { method = 'GET', body, signal, contexte = true } = {}) {
  if (contexte) exigerContexte(context);
  const response = await envoyer(`${API_BASE_URL}${path}`, {
    method,
    headers: {
      ...(contexte ? contextHeaders(context) : {}),
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    signal,
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw erreurHttp(response, data);
  return data;
}

export const notificationsApi = {
  /**
   * GET /api/notifications?non_lues=1&limite= → {notifications: [...], non_lues}
   * (non lues d'abord côté serveur ; non_lues : total, au-delà de la limite comprise).
   */
  liste(ctx, { nonLues = false, limite = null, signal } = {}) {
    const params = new URLSearchParams();
    if (nonLues) params.set('non_lues', '1');
    if (limite != null) params.set('limite', String(limite));
    // toString() plutôt que size : URLSearchParams.size manque à Safari avant la version 17
    const suite = params.toString();
    return requete(`/api/notifications${suite ? `?${suite}` : ''}`, ctx, { signal });
  },

  /** POST /api/notifications/:id/lue → {non_lues} */
  marquerLue(id, ctx) {
    return requete(`/api/notifications/${enc(id)}/lue`, ctx, { method: 'POST' });
  },

  /** POST /api/notifications/tout-lu → {non_lues} */
  toutMarquerLu(ctx) {
    return requete('/api/notifications/tout-lu', ctx, { method: 'POST' });
  },

  /** POST /api/alertes/:id/accuse → {alerte} : « Pris en compte » (idempotent côté serveur) */
  accuserAlerte(alerteId, ctx) {
    return requete(`/api/alertes/${enc(alerteId)}/accuse`, ctx, { method: 'POST' });
  },

  /**
   * GET /api/notifications/flux?apres=<curseur> : flux SSE, lu jusqu'à sa fermeture par le
   * serveur (la promesse se résout alors) ; onEvent(type, data) pour chaque `notification`.
   * onOuvert() : réponse acceptée, avant le premier octet du flux (le client relit alors la
   * liste pour combler l'intervalle entre sa dernière lecture et l'ouverture).
   * Erreur HTTP : ApiError {status, code} ; coupure : ApiError NETWORK ; annulation (signal) :
   * AbortError, relancée telle quelle.
   */
  async flux(ctx, { apres = null, signal, onEvent, onOuvert = null } = {}) {
    exigerContexte(ctx);
    const params = apres != null && apres !== '' ? `?${new URLSearchParams({ apres: String(apres) })}` : '';
    const response = await envoyer(`${API_BASE_URL}/api/notifications/flux${params}`, {
      headers: { ...contextHeaders(ctx), Accept: 'text/event-stream' },
      signal,
    });
    const isEventStream = (response.headers.get('content-type') || '').includes('text/event-stream');
    if (!response.ok || !isEventStream || !response.body) {
      const data = await response.json().catch(() => ({}));
      throw erreurHttp(response, response.ok ? { error: 'Flux de notifications indisponible', code: 'FLUX_INVALIDE' } : data);
    }
    onOuvert?.();
    await readSse(response, onEvent, EVENEMENTS_FLUX);
  },

  // ── Web Push (§6bis) ────────────────────────────────────────────────

  /** GET /api/push/cle-publique → {cle} : clé VAPID publique (base64url), sans contexte */
  clePush({ signal } = {}) {
    return requete('/api/push/cle-publique', null, { signal, contexte: false });
  },

  /**
   * POST /api/push/jeton → {jeton} : jeton signé de 5 minutes (entreprise, utilisateur), remis
   * par postMessage à la fenêtre d'activation, qui n'a pas le contexte de la plateforme.
   */
  jetonPush(ctx) {
    return requete('/api/push/jeton', ctx, { method: 'POST' });
  },

  /**
   * POST /api/push/abonnement {jeton, abonnement: {endpoint, keys: {p256dh, auth}}} : appelé
   * par la fenêtre d'activation, sans en-têtes de contexte (le jeton suffit).
   */
  abonnerPush(jeton, abonnement, { signal } = {}) {
    return requete('/api/push/abonnement', null, { method: 'POST', body: { jeton, abonnement }, signal, contexte: false });
  },

  /** GET /api/push/etat → {navigateurs} : navigateurs abonnés de l'utilisateur */
  etatPush(ctx, { signal } = {}) {
    return requete('/api/push/etat', ctx, { signal });
  },

  /** DELETE /api/push/abonnement {endpoint} : ce navigateur ne reçoit plus les notifications */
  desabonnerPush(endpoint, ctx) {
    return requete('/api/push/abonnement', ctx, { method: 'DELETE', body: { endpoint } });
  },

  // ── Moteur des agents (supervision P0, §11.3) ───────────────────────

  /**
   * GET /statut/agents (sans clé ni contexte) → {disponible, raison?} : 200 quand le moteur
   * bat, 503 {disponible: false, raison: MOTEUR_MUET | MOTEUR_ABSENT} sinon. Route absente
   * (404) ou réponse illisible : {disponible: null} (inconnu, aucun bandeau).
   */
  async etatMoteur({ signal } = {}) {
    const response = await envoyer(`${API_BASE_URL}/statut/agents`, { signal });
    const data = await response.json().catch(() => null);
    if (!data || typeof data.disponible !== 'boolean') return { disponible: null, raison: null };
    return { disponible: data.disponible, raison: typeof data.raison === 'string' ? data.raison : null };
  },
};
